import { List, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import UserDetails from '@/components/Details/UserDetails/UserDetails'
import OptionsMenu from '@/components/OptionsMenu'
import useLoginOnAction from '@/hooks/useLoginOnAction'
import useSpaceRegistry from '@/hooks/useSpaceRegistry'

import DetailsPage from '../../Details/DetailsPage'
import { Template } from './utils'

interface TemplateDetailsProps {
  publicPage?: boolean
}

const Component: React.FC<TemplateDetailsProps> = () => {
  const theme = useMantineTheme()
  const { id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { spacesMetadata } = useSpaceRegistry()
  const [spacesId, templateId] = id?.split('-') || []
  window.scrollTo(0, 0)

  const template = useMemo<Template | undefined>(() => {
    if (
      !spacesId ||
      !templateId ||
      !spacesMetadata ||
      !spacesMetadata[spacesId] ||
      !spacesMetadata[spacesId].metadataInfo?.templates[templateId]
    ) {
      return undefined
    }
    return spacesMetadata[spacesId].metadataInfo?.templates[templateId]
  }, [spacesId, spacesMetadata, templateId])
  const { name, description, image, creator, techSpec, tags } = useMemo(
    () =>
      template || {
        name: undefined,
        description: undefined,
        image: undefined,
        creator: undefined,
        techSpec: undefined,
        tags: undefined,
      },
    [template]
  )

  const templateState = useMemo(() => {
    if (tags) {
      return {
        id,
        tags,
      }
    }
    return {
      id,
    }
  }, [tags, id])

  // handle the navigation when user comes from sign out status
  const { handleNavigation } = useLoginOnAction('createItem', () =>
    navigate('/studio/createitem', {
      state: templateState,
    })
  )

  const actionButton = useMemo(
    () => ({
      text: 'Create Item',
      onClick: handleNavigation,
    }),
    [handleNavigation]
  )

  const optionsMenu = useMemo(() => <OptionsMenu copyLink={`${window.location}`} />, [])

  const descriptionProps = useMemo(() => ({ text: description }), [description])
  const techSpecParsed = useMemo<JSX.Element>(() => {
    if (!techSpec) return <></>
    return (
      <Stack gap="20px">
        <Title order={4}>{t('components.template.spec', 'Technical Specifications')}</Title>
        <List style={{ color: theme.colors.paragraph[0], fontSize: '16px' }}>
          {Object.entries(techSpec).map(([key, value]) => (
            <List.Item key={key}>
              <Stack style={{ flexDirection: 'row', gap: '5px' }}>
                <Text style={{ fontFamily: 'monument-grotesk-bold', paddingLeft: '0' }}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}:
                </Text>
                <Text>{value}</Text>
              </Stack>
            </List.Item>
          ))}
          <List.Item key="audit">
            <Stack style={{ flexDirection: 'row', gap: '5px' }}>
              <Text style={{ fontFamily: 'monument-grotesk-bold', paddingLeft: '0' }}>
                Security Audit:
              </Text>
              <Text>Level 1 (Unaudited)</Text>
            </Stack>
          </List.Item>
        </List>
      </Stack>
    )
  }, [techSpec, theme.colors.paragraph])

  const creatorDetails = useMemo(
    () =>
      creator &&
      creator.name && (
        <Stack gap="20px">
          <Title order={4}>{t('components.items.itemcreator', 'Creator')}</Title>
          <UserDetails userId={creator.name} />
        </Stack>
      ),
    [creator]
  )

  return (
    <DetailsPage
      image={image || undefined}
      title={name || undefined}
      description={descriptionProps}
      optionsMenu={optionsMenu}
      tags={tags}
      actionButton={actionButton}
    >
      {techSpecParsed}
      {creatorDetails}
      {/* <Title>{spacesMetadata?.[spacesId]?.metadataInfo?.name}</Title> */}
    </DetailsPage>
  )
}
Component.displayName = 'TemplatePage'

// eslint-disable-next-line import/prefer-default-export
export { Component }
